import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
} from 'lexical';
import { INSERT_UNORDERED_LIST_COMMAND, ListNode, ListItemNode } from '@lexical/list';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import {
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  ListBullets,
  ArrowCounterClockwise,
  ArrowClockwise,
  TextIndent,
  TextOutdent,
} from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';

interface LexicalEditorProps {
  initialContent?: string; // JSON string of Lexical state, or an HTML string when outputFormat is 'html'
  onChange?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  minHeight?: string;
  loading?: boolean;
  label?: string;
  isRequired?: boolean;
  errorText?: string;
  outputFormat?: 'json' | 'html';
  // Off when the editor sits in a page with other fields, so loading the page doesn't jump to it.
  autoFocus?: boolean;
}

// Toolbar component
function ToolbarPlugin({ isEditable }: { isEditable: boolean }) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatText = (format: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const formatIndent = () => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
  };

  const formatOutdent = () => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
  };

  const formatUndo = () => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  };

  const formatRedo = () => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  };

  if (!isEditable) {
    return null;
  }

  const btnBase =
    'p-1.5 rounded-md transition-colors duration-150 text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200';
  const btnActive = 'bg-blue-50 text-[#3762FB] hover:bg-blue-100';

  return (
    <div className="flex items-center gap-0.5 px-2.5 py-1.5 border-b border-[#E4EAF1] bg-[#F8FAFB] rounded-t-lg">
      <button
        type="button"
        onClick={() => formatText('bold')}
        className={twMerge(btnBase, isBold && btnActive)}
        title="Bold">
        <TextB size={16} weight={isBold ? 'fill' : 'regular'} />
      </button>
      <button
        type="button"
        onClick={() => formatText('italic')}
        className={twMerge(btnBase, isItalic && btnActive)}
        title="Italic">
        <TextItalic size={16} weight={isItalic ? 'fill' : 'regular'} />
      </button>
      <button
        type="button"
        onClick={() => formatText('underline')}
        className={twMerge(btnBase, isUnderline && btnActive)}
        title="Underline">
        <TextUnderline size={16} weight={isUnderline ? 'fill' : 'regular'} />
      </button>
      <button
        type="button"
        onClick={() => formatText('strikethrough')}
        className={twMerge(btnBase, isStrikethrough && btnActive)}
        title="Strikethrough">
        <TextStrikethrough size={16} weight={isStrikethrough ? 'fill' : 'regular'} />
      </button>

      <div className="w-px h-5 bg-[#E4EAF1] mx-1.5" />

      <button
        type="button"
        onClick={formatBulletList}
        className={twMerge(btnBase)}
        title="Bullet List">
        <ListBullets size={16} />
      </button>

      <div className="w-px h-5 bg-[#E4EAF1] mx-1.5" />

      <button type="button" onClick={formatIndent} className={twMerge(btnBase)} title="Indent">
        <TextIndent size={16} />
      </button>
      <button type="button" onClick={formatOutdent} className={twMerge(btnBase)} title="Outdent">
        <TextOutdent size={16} />
      </button>

      <div className="w-px h-5 bg-[#E4EAF1] mx-1.5" />

      <button type="button" onClick={formatUndo} className={twMerge(btnBase)} title="Undo">
        <ArrowCounterClockwise size={16} />
      </button>
      <button type="button" onClick={formatRedo} className={twMerge(btnBase)} title="Redo">
        <ArrowClockwise size={16} />
      </button>
    </div>
  );
}

// onChange Plugin
function OnChangePlugin({
  onChange,
  outputFormat,
}: {
  onChange: (content: string) => void;
  outputFormat: 'json' | 'html';
}) {
  const [editor] = useLexicalComposerContext();
  const onChangeRef = useRef(onChange);

  // Keep ref updated without triggering re-renders
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      if (outputFormat === 'html') {
        editorState.read(() => {
          onChangeRef.current($generateHtmlFromNodes(editor, null));
        });
        return;
      }
      editorState.read(() => {
        // Convert to JSON for storage
        const json = editorState.toJSON();
        onChangeRef.current(JSON.stringify(json));
      });
    });
  }, [editor, outputFormat]);

  return null;
}

// Initial State Plugin
function InitialStatePlugin({
  initialContent,
  outputFormat,
}: {
  initialContent?: string;
  outputFormat: 'json' | 'html';
}) {
  const [editor] = useLexicalComposerContext();
  const prevContentRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only update when content actually changes
    if (initialContent && prevContentRef.current !== initialContent) {
      prevContentRef.current = initialContent;

      // Use setTimeout to ensure editor is fully ready
      setTimeout(() => {
        if (outputFormat === 'html') {
          editor.update(() => {
            const root = $getRoot();
            root.clear();
            const dom = new DOMParser().parseFromString(initialContent, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            nodes.forEach((node) => root.append(node));
          });
          return;
        }
        try {
          const parsedState = JSON.parse(initialContent);
          const editorState = editor.parseEditorState(parsedState);
          editor.setEditorState(editorState);
        } catch {
          // initialContent is plain text, not Lexical JSON — set as text
          editor.update(() => {
            const root = $getRoot();
            root.clear();
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(initialContent));
            root.append(paragraph);
          });
        }
      }, 0);
    }
  }, [editor, initialContent, outputFormat]);

  return null;
}

function LexicalEditor({
  initialContent,
  onChange,
  placeholder = 'Start typing...',
  editable = true,
  className = '',
  minHeight = '150px',
  loading = false,
  label = '',
  isRequired = false,
  errorText = '',
  outputFormat = 'json',
  autoFocus = true,
}: LexicalEditorProps) {
  const handleChange = useCallback(
    (content: string) => {
      onChange?.(content);
    },
    [onChange]
  );

  const initialConfig = {
    namespace: 'DillEditor',
    nodes: [ListNode, ListItemNode],
    theme: {
      paragraph: 'mb-1',
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
        strikethrough: 'line-through',
      },
      list: {
        ul: 'list-disc ml-4',
        ol: 'list-decimal ml-4',
        listitem: 'mb-1',
      },
      link: 'text-blue-600 underline',
    },
    onError: (error: Error) => {
      console.error(error);
    },
  };

  const labelElement =
    label || isRequired ? (
      <label className="text-xs mb-0.5 text-left whitespace-nowrap font-medium">
        {isRequired && <span className="text-red-500">*</span>}
        {label}
      </label>
    ) : null;

  if (loading) {
    return (
      <div className="w-full flex flex-col">
        {labelElement}
        <div className={twMerge('border border-[#E4EAF1] rounded-lg overflow-hidden', className)}>
          <div className="px-2.5 py-1.5 border-b border-[#E4EAF1] bg-[#F8FAFB] h-10 flex items-center gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-7 h-7 bg-[#E4EAF1] rounded-md animate-pulse" />
            ))}
          </div>
          <div className="p-4 space-y-2.5" style={{ minHeight }}>
            <div className="h-3.5 bg-[#E4EAF1] rounded animate-pulse w-3/4" />
            <div className="h-3.5 bg-[#E4EAF1] rounded animate-pulse w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      {labelElement}
      <div
        className={twMerge(
          'group/editor border rounded-lg overflow-hidden relative transition-colors duration-200',
          errorText ? 'border-[#F04438]' : 'border-[#E4EAF1] focus-within:border-[#3762FB]/40',
          !editable && 'bg-gray-50',
          className
        )}>
        <LexicalComposer initialConfig={initialConfig}>
          <ToolbarPlugin isEditable={editable} />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={twMerge(
                  'relative outline-none px-4 py-3 overflow-y-auto leading-relaxed text-[13px] text-[#101218]',
                  !editable && 'bg-gray-50 text-gray-600 cursor-default'
                )}
                style={{ minHeight }}
                aria-placeholder={placeholder}
                placeholder={
                  <div className="absolute top-14 left-4 text-[#94A3B8] pointer-events-none text-[13px]">
                    {placeholder}
                  </div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          {editable && autoFocus && <AutoFocusPlugin />}
          <ListPlugin />
          {initialContent && (
            <InitialStatePlugin initialContent={initialContent} outputFormat={outputFormat} />
          )}
          {editable && onChange && (
            <OnChangePlugin onChange={handleChange} outputFormat={outputFormat} />
          )}
        </LexicalComposer>
      </div>
      {errorText && <p className="mt-0.5 text-[0.6em] font-normal text-red-500">{errorText}</p>}
    </div>
  );
}

export default LexicalEditor;
